// Onglets « Pièces » et « Étapes et validateurs » de l'éditeur de version : pièces typées
// (catalogue des types), obligatoires ou conditionnelles ; étapes ordonnées avec nature, service
// chargé, rôle attendu, délai interne et action de notification. La dernière étape est la décision.
import { NATURES_ETAPE, tousLesChamps, type EtapeDemarche, type PieceDemarche } from '@cc/contrats';
import { Bouton, CaseACocher, Champ, Selection, ZoneTexte } from '@cc/ui';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { etapeVide, LIBELLES_NATURE_ETAPE, pieceVide, useServices, useTypesPieces } from '../contenu';
import { genererCode } from '../formulaire/constructeur';
import { EditeurCondition, texteCondition } from '../formulaire/EditeurCondition';
import type { ProprietesOnglet } from './OngletIdentite';
import styles from '../demarches.module.css';

export function OngletPieces({ contenu, onChanger, lectureSeule }: ProprietesOnglet) {
  const types = useTypesPieces();
  const champs = tousLesChamps(contenu.formulaire).map((x) => x.champ);
  const maj = (pieces: PieceDemarche[]) => onChanger({ ...contenu, pieces });
  const majPiece = (i: number, patch: Partial<PieceDemarche>) => maj(contenu.pieces.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const options = (types.data ?? []).map((t) => ({ valeur: t.code, libelle: `${t.libelle}${t.local ? ' (local)' : ''}` }));
  return (
    <div className={styles.pile}>
      <p className={styles.aideDiscrete}>Les pièces sont désignées par un type du catalogue (national ou propre à la collectivité). Une pièce conditionnelle n'est demandée que si sa condition sur le formulaire est remplie.</p>
      {contenu.pieces.length === 0 ? <p className={styles.aideDiscrete}>Aucune pièce demandée.</p> : null}
      <ul className={styles.liste}>
        {contenu.pieces.map((p, i) => (
          <li key={i} className={styles.carte}>
            <div className={styles.carteEntete}>
              <h3>
                <span className={styles.rang}>{i + 1}</span>
                {p.libelle || 'Pièce sans libellé'} {p.obligatoire ? '(obligatoire)' : '(facultative)'}
                {p.condition ? `, si ${texteCondition(p.condition, champs)}` : ''}
              </h3>
              {!lectureSeule ? (
                <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la pièce ${i + 1}`} onClick={() => maj(contenu.pieces.filter((_, j) => j !== i))}>
                  Retirer
                </Bouton>
              ) : null}
            </div>
            {!lectureSeule ? (
              <>
                <div className={styles.deuxChamps}>
                  <Selection
                    libelle="Type de pièce"
                    value={p.type}
                    required
                    optionVide="Choisir un type"
                    options={options.some((o) => o.valeur === p.type) || !p.type ? options : [...options, { valeur: p.type, libelle: p.type }]}
                    aide={types.error ? 'Catalogue des types indisponible : saisissez le code du type ci-dessous.' : undefined}
                    onChange={(e) => {
                      const t = types.data?.find((x) => x.code === e.target.value);
                      majPiece(i, { type: e.target.value, libelle: p.libelle || t?.libelle || '' });
                    }}
                  />
                  <Champ libelle="Libellé affiché à l'usager" value={p.libelle} required onChange={(e) => majPiece(i, { libelle: e.target.value })} />
                </div>
                {types.error ? <Champ libelle="Code du type de pièce" className="cc-mono" value={p.type} onChange={(e) => majPiece(i, { type: e.target.value })} /> : null}
                <CaseACocher libelle="Obligatoire" checked={p.obligatoire} onChange={(e) => majPiece(i, { obligatoire: e.target.checked })} />
                <ZoneTexte libelle="Aide (où obtenir la pièce, format attendu)" value={p.aide ?? ''} facultatif rows={2} onChange={(e) => majPiece(i, { aide: e.target.value || undefined })} />
                <EditeurCondition libelle="Pièce demandée seulement si" condition={p.condition} onChanger={(condition) => majPiece(i, { condition })} champs={champs} />
              </>
            ) : null}
          </li>
        ))}
      </ul>
      {!lectureSeule ? (
        <div>
          <Bouton variante="secondaire" icone={Plus} onClick={() => maj([...contenu.pieces, pieceVide()])}>
            Ajouter une pièce
          </Bouton>
        </div>
      ) : null}
    </div>
  );
}

export function OngletEtapes({ contenu, onChanger, lectureSeule }: ProprietesOnglet) {
  const services = useServices();
  const maj = (etapes: EtapeDemarche[]) => onChanger({ ...contenu, etapes });
  const majEtape = (i: number, patch: Partial<EtapeDemarche>) => maj(contenu.etapes.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const deplacer = (i: number, sens: -1 | 1) => {
    const j = i + sens;
    if (j < 0 || j >= contenu.etapes.length) return;
    const copie = [...contenu.etapes];
    const a = copie[i] as EtapeDemarche;
    copie[i] = copie[j] as EtapeDemarche;
    copie[j] = a;
    maj(copie);
  };
  const derniere = contenu.etapes.length - 1;
  return (
    <div className={styles.pile}>
      <p className={styles.aideDiscrete}>
        Chaque étape devient un état du dossier (instruction, validation ou décision) ; le dossier est affecté au service de l'étape à son entrée. La dernière étape est toujours la décision de l'autorité. Un retour vers une étape déjà parcourue reste possible avec un motif.
      </p>
      {services.error ? <p className={styles.aideDiscrete}>La liste des services n'a pas pu être lue : les étapes peuvent rester sans service, le dossier gardera alors son service de dépôt.</p> : null}
      <ol className={styles.liste}>
        {contenu.etapes.map((e, i) => (
          <li key={i} className={styles.carte}>
            <div className={styles.carteEntete}>
              <h3>
                <span className={styles.rang}>{i + 1}</span>
                {e.libelle || 'Étape sans libellé'} ({LIBELLES_NATURE_ETAPE[e.nature]})
              </h3>
              {!lectureSeule ? (
                <div className={styles.actionsSection}>
                  <Bouton petit variante="discret" icone={ArrowUp} aria-label={`Monter l'étape ${i + 1}`} disabled={i === 0 || i === derniere} onClick={() => deplacer(i, -1)}>
                    Monter
                  </Bouton>
                  <Bouton petit variante="discret" icone={ArrowDown} aria-label={`Descendre l'étape ${i + 1}`} disabled={i >= derniere - 1} onClick={() => deplacer(i, 1)}>
                    Descendre
                  </Bouton>
                  <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer l'étape ${i + 1}`} disabled={i === derniere} onClick={() => maj(contenu.etapes.filter((_, j) => j !== i))}>
                    Retirer
                  </Bouton>
                </div>
              ) : null}
            </div>
            {!lectureSeule ? (
              <>
                <div className={styles.deuxChamps}>
                  <Champ
                    libelle="Libellé de l'étape"
                    value={e.libelle}
                    required
                    onChange={(ev) => majEtape(i, { libelle: ev.target.value, code: e.code.startsWith('etape_') || !e.code ? genererCode(ev.target.value, contenu.etapes.filter((_, j) => j !== i).map((x) => x.code), 'etape').slice(0, 31) : e.code })}
                  />
                  <Champ libelle="Code" className="cc-mono" value={e.code} required maxLength={31} aide="Identifie l'état du dossier ; conservé d'une version à l'autre pour permettre les migrations." onChange={(ev) => majEtape(i, { code: ev.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
                </div>
                <div className={styles.deuxChamps}>
                  <Selection libelle="Nature" value={e.nature} disabled={i === derniere} options={NATURES_ETAPE.map((n) => ({ valeur: n, libelle: LIBELLES_NATURE_ETAPE[n] ?? n, desactivee: n === 'decision' && i !== derniere }))} aide={i === derniere ? 'La dernière étape est la décision.' : undefined} onChange={(ev) => majEtape(i, { nature: ev.target.value as EtapeDemarche['nature'] })} />
                  <Selection libelle="Service chargé de l'étape" value={e.service_id ?? ''} optionVide="Aucun service (le dossier garde son service)" options={(services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom }))} onChange={(ev) => majEtape(i, { service_id: ev.target.value || null })} />
                </div>
                <div className={styles.deuxChamps}>
                  <Champ libelle="Rôle attendu (indicatif)" className="cc-mono" value={e.role_code ?? ''} facultatif aide="Par exemple chef_service ; la permission découle de la nature de l'étape." onChange={(ev) => majEtape(i, { role_code: ev.target.value.trim() || null })} />
                  <Champ libelle="Délai interne de l'étape (jours)" type="number" min={1} max={365} value={e.delai_interne_jours ?? ''} facultatif onChange={(ev) => majEtape(i, { delai_interne_jours: ev.target.value === '' ? null : Number(ev.target.value) })} />
                </div>
                <CaseACocher libelle="Notifier l'usager à l'entrée dans cette étape" checked={e.actions.includes('notifier')} onChange={(ev) => majEtape(i, { actions: ev.target.checked ? ['notifier'] : [] })} />
              </>
            ) : (
              <p className={styles.aideDiscrete}>
                Service : {services.data?.find((s) => s.id === e.service_id)?.nom ?? 'aucun'}
                {e.delai_interne_jours ? `, délai interne ${e.delai_interne_jours} jours` : ''}
                {e.actions.includes('notifier') ? ', usager notifié' : ''}
              </p>
            )}
          </li>
        ))}
      </ol>
      {!lectureSeule ? (
        <div>
          <Bouton variante="secondaire" icone={Plus} onClick={() => maj([...contenu.etapes.slice(0, derniere), etapeVide(contenu.etapes.length), ...contenu.etapes.slice(derniere)])}>
            Ajouter une étape avant la décision
          </Bouton>
        </div>
      ) : null}
    </div>
  );
}
